import type { RouterContextType } from "@/api/permissions/permissions.api";
import { shortLinkColumns } from "@/components/features/ShortLinks/shortLinkColumns";
import { CreateShortLinkModal } from "@/components/features/ShortLinks/CreateShortLinkModal";
import {
  LINK_STATUS_OPTIONS,
  SHORT_LINK_PAGE_SIZE,
  type LinkStatusFilter,
} from "@/components/features/ShortLinks/shortLinks.constants";
import styles from "@/components/features/ShortLinks/ShortLinks.module.scss";
import { Button } from "@/components/ui/Button/Button";
import Table from "@/components/ui/Table/Table";
import { useShortLinkCampaigns, useShortLinks } from "@/hooks/deeplink/useShortLinks";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { Select, TextInput, Title } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { requireSuperAdmin } from "@/utils/shortLinkAccess";

export const Route = createFileRoute("/_authenticated/short-links/")({
  component: ShortLinksPage,
  beforeLoad: async (args) => {
    await requireSuperAdmin({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const ALL_CAMPAIGNS = "__all__";

function ShortLinksPage() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [campaign, setCampaign] = useState<string>(ALL_CAMPAIGNS);
  const [status, setStatus] = useState<LinkStatusFilter>("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Debounced so a search does not fire a request per keystroke.
  const [debouncedSearch] = useDebouncedValue(search, 300);

  useBreadcrumb([{ title: "Short links" }]);

  const { data: campaigns } = useShortLinkCampaigns();

  const query = useMemo(
    () => ({
      page: page + 1,
      pageSize: SHORT_LINK_PAGE_SIZE,
      // Undefined rather than "" for every optional filter: the API treats a
      // blank campaign as a real predicate that matches nothing, so sending ""
      // would empty the table instead of clearing the filter.
      search: debouncedSearch.trim() || undefined,
      campaign: campaign === ALL_CAMPAIGNS ? undefined : campaign,
      isActive: status === "all" ? undefined : status === "active",
    }),
    [page, debouncedSearch, campaign, status],
  );

  const { data, isLoading } = useShortLinks(query);

  const campaignOptions = useMemo(
    () => [
      { value: ALL_CAMPAIGNS, label: "All campaigns" },
      ...(campaigns ?? []).map((name) => ({ value: name, label: name })),
    ],
    [campaigns],
  );

  // Any filter change must return to the first page, or a narrowed result set
  // lands the user on a page that no longer exists and reads as empty.
  const resetToFirstPage = () => setPage(0);

  return (
    <div>
      <Title order={2}>Short links</Title>

      <div className={styles.toolbar}>
        <TextInput
          className={styles.searchField}
          label="Search"
          placeholder="Code, destination or label"
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            resetToFirstPage();
          }}
        />
        <Select
          className={styles.filterField}
          label="Campaign"
          data={campaignOptions}
          value={campaign}
          onChange={(value) => {
            setCampaign(value ?? ALL_CAMPAIGNS);
            resetToFirstPage();
          }}
        />
        <Select
          className={styles.filterField}
          label="Status"
          data={[...LINK_STATUS_OPTIONS]}
          value={status}
          onChange={(value) => {
            setStatus((value as LinkStatusFilter) ?? "all");
            resetToFirstPage();
          }}
        />
        <Button onClick={() => setIsCreateOpen(true)}>Create link</Button>
      </div>

      <Table
        data={data?.items ?? []}
        columns={shortLinkColumns}
        isLoading={isLoading}
        manualPagination
        enablePagination
        pageSize={SHORT_LINK_PAGE_SIZE}
        pageIndex={page}
        pageCount={Math.ceil((data?.total ?? 0) / SHORT_LINK_PAGE_SIZE)}
        totalRecords={data?.total ?? 0}
        onPageChange={setPage}
      />

      <CreateShortLinkModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </div>
  );
}
